/**
 * CENTRAL PAGE CONFIGURATION
 * Single source of truth for sidebar navigation AND user permissions.
 * Both MainLayout.jsx and UsersPage.jsx import from here.
 * To add a new page: add it here and it auto-appears in sidebar + permissions.
 */
import {
  LayoutDashboard, Scissors, Package, Factory, CheckCircle, Archive,
  Truck, Brain, Users, FileText, Settings, Printer, Ruler, IndianRupee,
  Search, Award, Target, PackagePlus, Cog, ClipboardList, AlertTriangle,
  Tag, LayoutGrid, ArrowRight, Database, Zap, Upload, Bell, ScanLine,
  ShieldCheck, BarChart3, Shirt, ShoppingCart, ArrowRightLeft, Layers, TrendingUp,
  Calendar, Mail, FileSearch, ListChecks, ScanBarcode, ClipboardCheck, Trophy, Activity,
} from "lucide-react";
import { ADS_COMMAND_CENTER_ENABLED } from "./featureFlags";
import { OMS_SECTIONS } from "./omsSections";

// Every navigable page in the app
const BASE_PAGE_REGISTRY = [
  // ──── Core ────
  { key: "dashboard",         path: "/dashboard",          label: "Command Center",       icon: LayoutDashboard, group: "Core",            roles: ["admin","production_manager","supervisor"] },
  { key: "control-tower",     path: "/control-tower",      label: "Control Tower",        icon: AlertTriangle,   group: "Core",            roles: ["admin","production_manager"] },

  // ──── Fabric ────
  { key: "fabric-receive",    path: "/fabric-receive",     label: "Fabric Receive",       icon: PackagePlus,     group: "Fabric",          roles: ["admin","production_manager","supervisor"] },
  { key: "fabric-inventory",  path: "/fabric-inventory",   label: "Fabric Stock",         icon: Archive,         group: "Fabric",          roles: ["admin","production_manager","supervisor"] },
  { key: "fabric-alerts",     path: "/fabric-alerts",      label: "Fabric Alerts",        icon: AlertTriangle,   group: "Fabric",          roles: ["admin","production_manager","supervisor"] },
  { key: "godown-transfer",   path: "/godown-transfer",    label: "Godown Transfer",      icon: ArrowRightLeft,  group: "Fabric",          roles: ["admin","production_manager","supervisor","operator"] },

  // ──── Cutting ────
  { key: "cutting-orders",    path: "/cutting-orders",     label: "Cutting Orders",       icon: ClipboardList,   group: "Cutting",         roles: ["admin","production_manager","supervisor"] },
  { key: "cutting-entry",     path: "/cutting-entry",      label: "Cutting Entry",        icon: Scissors,        group: "Cutting",         roles: ["admin","production_manager","supervisor"] },
  { key: "bulk-labels",       path: "/bulk-labels",        label: "Print Labels",         icon: Printer,         group: "Cutting",         roles: ["admin","production_manager","supervisor"] },
  { key: "cutting-issue",     path: "/cutting-issue",      label: "Cutting Issue",        icon: ArrowRight,      group: "Cutting",         roles: ["admin","production_manager","supervisor","operator"] },
  { key: "bundles",           path: "/bundles",            label: "Bundle Management",    icon: Package,         group: "Cutting",         roles: ["admin","production_manager","supervisor","operator"] },

  // ──── Production ────
  { key: "production-receive",path: "/production-receive", label: "Production Receive",   icon: CheckCircle,     group: "Production",      roles: ["admin","production_manager","supervisor","operator"] },
  { key: "bundle-work",      path: "/bundle-work",        label: "Bundle Work",          icon: Layers,          group: "Production",      roles: ["admin","production_manager","supervisor","operator","karigar"] },
  { key: "production",        path: "/production",         label: "Production",           icon: Factory,         group: "Production",      roles: ["admin","production_manager","supervisor","operator"] },
  { key: "quality-control",   path: "/quality-control",    label: "Quality Control",      icon: ShieldCheck,     group: "Production",      roles: ["admin","production_manager","supervisor","operator"] },
  { key: "finished-inventory",path: "/finished-inventory", label: "Finished Inventory",   icon: Archive,         group: "Production",      roles: ["admin","production_manager","supervisor"] },

  // ──── Special Entries ────
  { key: "urgent-entry",      path: "/urgent-entry",       label: "Urgent Entry",         icon: Zap,             group: "Special Entries",  roles: ["admin","production_manager","supervisor","operator"] },
  { key: "dhaga-cutting",     path: "/dhaga-cutting",      label: "Dhaga Cutting",        icon: Scissors,        group: "Special Entries",  roles: ["admin","production_manager","supervisor","operator"] },
  { key: "label-taka-gather", path: "/label-taka-gather",  label: "Label Taka & Gather",  icon: Tag,             group: "Special Entries",  roles: ["admin","production_manager","supervisor","operator"] },
  { key: "flat-entry",        path: "/flat-entry",         label: "Flat Entry",           icon: LayoutGrid,      group: "Special Entries",  roles: ["admin","production_manager","supervisor","operator"] },
  { key: "alter-entry",       path: "/alter-entry",        label: "Alter Entry",          icon: Ruler,           group: "Special Entries",  roles: ["admin","production_manager","supervisor","operator"] },
  { key: "press-entry",       path: "/press-entry",        label: "Press Entry",          icon: Shirt,           group: "Special Entries",  roles: ["admin","production_manager","supervisor","operator"] },

  // ──── People & HR ────
  { key: "hr-payroll",        path: "/hr-payroll",         label: "HR & Payroll",          icon: Users,           group: "People & HR",      roles: ["admin"] },

  // ──── Finance ────
  { key: "karigar-payments",  path: "/karigar-payments",   label: "Karigar Payments",     icon: IndianRupee,     group: "Finance",         roles: ["admin","production_manager","supervisor"] },
  { key: "karigar-payout-debug", path: "/karigar-payout-debug", label: "Payout Debug (Audit)", icon: IndianRupee, group: "Finance",     roles: ["admin","production_manager"] },
  { key: "opening-balance-admin", path: "/opening-balance-admin", label: "Opening Balance Manager", icon: IndianRupee, group: "Finance", roles: ["admin","production_manager"] },
  { key: "style-rate-audit", path: "/style-rate-audit", label: "Style Rate Audit", icon: IndianRupee, group: "Finance", roles: ["admin","production_manager"] },
  { key: "bundle-audit", path: "/bundle-audit", label: "Bundle Audit (Debug)", icon: IndianRupee, group: "Production", roles: ["admin","production_manager"] },
  { key: "press-payments",    path: "/press-payments",     label: "Press Payments",       icon: Shirt,           group: "Finance",         roles: ["admin","production_manager"] },
  { key: "advance",           path: "/advance",            label: "Advance",              icon: IndianRupee,     group: "Finance",         roles: ["admin","production_manager"] },

  // ──── Dispatch ────
  { key: "dispatch",          path: "/dispatch",           label: "Dispatch",             icon: Truck,           group: "Dispatch",        roles: ["admin","production_manager"] },
  { key: "unicommerce",       path: "/unicommerce",        label: "Unicommerce",          icon: ShoppingCart,    group: "Dispatch",        roles: ["admin","production_manager"] },
  { key: "order-processing",   path: "/order-processing",    label: "Order Processing",     icon: ClipboardList,   group: "Dispatch",        roles: ["admin","production_manager"] },

  // ──── Insights & Reports ────
  // Hidden (Feb 2026): AI Analytics, AI Planning, Sales Analytics, Efficiency Scores.
  // Kept in registry with `hidden: true` so ProtectedRoute pageKey lookup + backend
  // permission records remain intact; sidebar + permission UI filter these out.
  { key: "copilot",          path: "/copilot",            label: "AGM ONE Copilot",      icon: Brain,           group: "Insights & Reports", roles: ["admin","production_manager","supervisor","operator","analytics_viewer"] },
  { key: "ai-analytics",      path: "/ai-analytics",       label: "AI Analytics",         icon: Brain,           group: "Insights & Reports", roles: ["admin","production_manager"], hidden: true },
  { key: "production-planning",path: "/production-planning",label: "Production Planning",  icon: Target,          group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "style-profitability",path: "/style-profitability",label: "Style Profitability",  icon: TrendingUp,      group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "fabric-consumption-intelligence", path: "/style-profitability?view=fabric-consumption-intelligence", label: "Fabric Consumption Intelligence", icon: BarChart3, group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "vendor-intelligence", path: "/style-profitability?view=vendor-intelligence", label: "Vendor Intelligence", icon: BarChart3, group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "daily-production", path: "/daily-production",  label: "Daily Production",    icon: Scissors,        group: "Insights & Reports", roles: ["admin","production_manager","supervisor"] },
  { key: "sales-analytics",  path: "/sales-analytics",   label: "Sales Analytics",     icon: TrendingUp,      group: "Insights & Reports", roles: ["admin","production_manager"], hidden: true },
  { key: "worker-performance", path: "/worker-performance", label: "Worker Performance",   icon: Users,           group: "Insights & Reports", roles: ["admin","production_manager","supervisor"] },
  { key: "worker-efficiency",  path: "/worker-efficiency",  label: "Efficiency Scores",    icon: Award,           group: "Insights & Reports", roles: ["admin","production_manager","supervisor"], hidden: true },
  { key: "operator-errors",   path: "/operator-errors",    label: "Operator Errors",      icon: AlertTriangle,   group: "Insights & Reports", roles: ["admin"] },
  { key: "reports",           path: "/reports",            label: "Reports",              icon: FileText,        group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "analytics-dashboard",path: "/analytics-dashboard",label: "Analytics Report",    icon: BarChart3,       group: "Insights & Reports", roles: ["admin","production_manager","supervisor"] },

  // ──── Settings ────
  { key: "machines",          path: "/machines",           label: "Machines",             icon: Cog,             group: "Settings",        roles: ["admin","production_manager"] },
  { key: "master-data",       path: "/master-data",        label: "Master Data",          icon: Database,        group: "Settings",        roles: ["admin","production_manager"] },
  { key: "product-control",   path: "/product-control",    label: "Product Control",      icon: Ruler,           group: "Product Control", roles: ["admin","production_manager","product_data_operator"] },
  { key: "business-intelligence", path: "/business-intelligence", label: "Business Intelligence", icon: TrendingUp,   group: "Analytics",       roles: ["admin","production_manager","analytics_viewer"] },
  { key: "batch-import",      path: "/batch-import",       label: "Batch Import",         icon: Upload,          group: "Settings",        roles: ["admin","production_manager"] },
  { key: "bulk-upload",       path: "/bulk-upload",        label: "Bulk Data Upload",     icon: Upload,          group: "Settings",        roles: ["admin"] },
  { key: "alerts",            path: "/alerts",             label: "Action Center",        icon: Bell,            group: "Core",            roles: ["admin","production_manager","supervisor"] },
  { key: "users",             path: "/users",              label: "User Management",      icon: Settings,        group: "Settings",        roles: ["admin"] },
  { key: "data-reset",        path: "/data-reset",         label: "Data Reset",           icon: Database,        group: "Settings",        roles: ["admin"] },
  { key: "marketplace-operations", path: "/marketplace-operations", label: "Marketplace Operations", icon: ShoppingCart, group: "Dispatch", roles: ["admin","production_manager"] },
  { key: "marketplace-label-jobs", path: "/marketplace-label-jobs", label: "Marketplace Label Jobs", icon: Printer, group: "Dispatch", roles: ["admin","production_manager"] },
  { key: "desktop-stations", path: "/desktop-stations", label: "Desktop Stations", icon: Printer, group: "Dispatch", roles: ["admin"] },
  { key: "system-health",     path: "/system-health",      label: "System Health",        icon: Activity,        group: "Settings",        roles: ["admin"] },
  { key: "operations-settings", path: "/operations-settings", label: "Operations Settings", icon: Settings, group: "Settings", roles: ["admin"] },
  { key: "mobile-v2",         path: "/mobile-v2",          label: "Mobile V2 (Preview)",  icon: LayoutGrid,      group: "Settings",        roles: ["admin"] },
  { key: "ui-rollout",        path: "/mobile-v2/settings/rollout", label: "UI Rollout Manager", icon: Cog,        group: "Settings",        roles: ["admin"] },
  { key: "karigar-availability", path: "/karigar-availability", label: "Karigar Availability", icon: Users, group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "karigar-leaves",    path: "/karigar-leaves",     label: "Karigar Leaves",       icon: Calendar,        group: "Settings",        roles: ["admin","production_manager"] },
  { key: "karigar-settings",  path: "/karigar-settings",   label: "Karigar Settings",     icon: Settings,        group: "Settings",        roles: ["admin","production_manager"] },
  { key: "press-master",      path: "/press-master",       label: "Press Operators",      icon: Shirt,           group: "Settings",        roles: ["admin","production_manager"] },
  { key: "press-report",      path: "/press-report",       label: "Press Report",         icon: Shirt,           group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "best-employee",     path: "/best-employee",      label: "Best Employee",        icon: Award,           group: "Insights & Reports", roles: ["admin","production_manager"] },
  { key: "otp-dashboard",     path: "/otp-center",         label: "OTP Center",           icon: ShieldCheck,     group: "OTP Center",      roles: ["admin","production_manager"] },
  { key: "otp-accounts",      path: "/otp-center/accounts",label: "Mail Accounts",        icon: Mail,            group: "OTP Center",      roles: ["admin","production_manager"] },
  { key: "otp-audit",         path: "/otp-center/audit",   label: "Audit Logs",           icon: FileSearch,      group: "OTP Center",      roles: ["admin"] },

  // ──── Picklist ────
  { key: "picklist-list",     path: "/picklist",           label: "Picklists",            icon: ListChecks,      group: "Picklist",        roles: ["admin","production_manager","operator"] },

  // ──── Return Scanning ────
  { key: "return-scan",       path: "/return-scan",        label: "Return Scanning",      icon: ScanBarcode,     group: "Picklist",        roles: ["admin","production_manager","operator"] },
  { key: "bulk-putaway",      path: "/bulk-putaway",       label: "Bulk Putaway Export",  icon: ScanBarcode,     group: "Picklist",        roles: ["admin","production_manager"] },
  { key: "return-analytics",  path: "/return-analytics",   label: "Return Analytics",     icon: ScanBarcode,     group: "Picklist",        roles: ["admin","production_manager","operator"] },

  // ──── Task Management (Feb 2026) ────
  { key: "task-management",   path: "/task-management",    label: "Task Management",      icon: ClipboardCheck,  group: "Task Management", roles: ["admin","production_manager","supervisor","operator","staff"] },
  { key: "targets-projections", path: "/targets-projections", label: "Targets & Projections", icon: Trophy,     group: "Task Management", roles: ["admin","production_manager","supervisor","operator","staff"] },

  // ──── Ads Command Center v2 — build-time gated, backend remains independently gated ────
  ...(ADS_COMMAND_CENTER_ENABLED ? [{ key: "ads-command-center", path: "/ads-command-center", label: "Ads Command Center", icon: TrendingUp, group: "Ads Command Center", roles: ["admin"] }] : []),
];

export const DOMAIN_DEFINITIONS = [
  { id: "home", label: "Home", icon: LayoutDashboard },
  { id: "erp", label: "ERP", icon: Factory },
  { id: "oms", label: "OMS", icon: ShoppingCart },
  { id: "wms", label: "WMS", icon: Package },
  { id: "campaigns", label: "Campaigns", icon: TrendingUp },
  { id: "reports", label: "Reports", icon: BarChart3 },
  { id: "masters", label: "Masters", icon: Database },
  { id: "settings", label: "Settings", icon: Settings },
];

const GROUP_DOMAIN_DEFAULTS = {
  "Core": "home",
  "Fabric": "erp",
  "Cutting": "erp",
  "Production": "erp",
  "Special Entries": "erp",
  "Finance": "erp",
  "People & HR": "erp",
  "Dispatch": "oms",
  "Insights & Reports": "reports",
  "Settings": "settings",
  "Product Control": "masters",
  "Analytics": "reports",
  "Task Management": "erp",
  "Ads Command Center": "campaigns",
  "OTP Center": "oms",
  "Picklist": "wms",
};

const PAGE_DOMAIN_OVERRIDES = {
  "godown-transfer": "wms",
  "bulk-labels": "erp",
  "finished-inventory": "wms",
  "dispatch": "wms",
  "marketplace-operations": "oms",
  "marketplace-label-jobs": "wms",
  "desktop-stations": "oms",
  "production-planning": "erp",
  "karigar-availability": "erp",
  "karigar-leaves": "erp",
  "machines": "masters",
  "master-data": "masters",
  "product-control": "masters",
  "business-intelligence": "reports",
  "press-master": "masters",
  "press-report": "reports",
  "best-employee": "reports",
  "return-analytics": "reports",
};

const GROUP_SUBGROUP_DEFAULTS = {
  "Core": "Overview",
  "Fabric": "Fabric & Cutting",
  "Cutting": "Fabric & Cutting",
  "Production": "Production & Quality",
  "Special Entries": "Special Operations",
  "Finance": "People & Payroll",
  "People & HR": "People & Payroll",
  "Dispatch": "Orders & Sources",
  "Insights & Reports": "Production & Operations",
  "Settings": "System & Operations",
  "Product Control": "Product Masters",
  "Analytics": "Business Intelligence",
  "Task Management": "Planning & Control",
  "Ads Command Center": "Campaign Operations",
  "OTP Center": "Automation & Sessions",
  "Picklist": "Picking & Putaway",
};

const PAGE_SUBGROUP_OVERRIDES = {
  "dashboard": "Overview",
  "control-tower": "Overview",
  "godown-transfer": "Inventory",
  "bulk-labels": "Fabric & Cutting",
  "finished-inventory": "Inventory",
  "dispatch": "Fulfilment & Labels",
  "unicommerce": "OMS Settings",
  "order-processing": "Order Processing",
  "marketplace-operations": "OMS Settings",
  "marketplace-label-jobs": "Fulfilment & Labels",
  "desktop-stations": "OMS Settings",
  "production-planning": "Planning & Control",
  "task-management": "Planning & Control",
  "targets-projections": "Planning & Control",
  "karigar-availability": "People & Payroll",
  "karigar-leaves": "People & Payroll",
  "master-data": "Business Masters",
  "product-control": "Product Masters",
  "business-intelligence": "Business Intelligence",
  "copilot": "Business Intelligence",
  "style-profitability": "Profitability & Supply",
  "fabric-consumption-intelligence": "Profitability & Supply",
  "vendor-intelligence": "Profitability & Supply",
  "daily-production": "Production & Operations",
  "reports": "Production & Operations",
  "analytics-dashboard": "Production & Operations",
  "worker-performance": "Workforce & Returns",
  "operator-errors": "Workforce & Returns",
  "press-report": "Workforce & Returns",
  "best-employee": "Workforce & Returns",
  "press-master": "Production Masters",
  "machines": "Production Masters",
  "users": "Users & Access",
  "system-health": "System & Operations",
  "operations-settings": "System & Operations",
  "data-reset": "System & Operations",
  "batch-import": "Data & Imports",
  "bulk-upload": "Data & Imports",
  "mobile-v2": "UI & Rollout",
  "ui-rollout": "UI & Rollout",
  "karigar-settings": "System & Operations",
  "otp-dashboard": "OMS Settings",
  "otp-accounts": "OMS Settings",
  "otp-audit": "OMS Settings",
  "picklist-list": "Picking & Putaway",
  "return-scan": "Returns",
  "bulk-putaway": "Picking & Putaway",
  "return-analytics": "Workforce & Returns",
};

const DOMAIN_MOBILE_DEFAULTS = {
  home: "MOBILE_FULL",
  erp: "MOBILE_OPERATIONAL",
  oms: "DESKTOP_ONLY",
  wms: "MOBILE_OPERATIONAL",
  campaigns: "DESKTOP_ONLY",
  reports: "MOBILE_VIEW_ONLY",
  masters: "DESKTOP_ONLY",
  settings: "DESKTOP_ONLY",
};

const PAGE_MOBILE_OVERRIDES = {
  "dashboard": "MOBILE_FULL",
  "control-tower": "MOBILE_FULL",
  "hr-payroll": "DESKTOP_ONLY",
  "cutting-issue": "MOBILE_OPERATIONAL",
  "production-receive": "MOBILE_OPERATIONAL",
  "urgent-entry": "MOBILE_OPERATIONAL",
  "fabric-receive": "MOBILE_OPERATIONAL",
  "fabric-inventory": "MOBILE_FULL",
  "finished-inventory": "MOBILE_FULL",
  "picklist-list": "MOBILE_OPERATIONAL",
  "return-scan": "MOBILE_OPERATIONAL",
  "bulk-putaway": "MOBILE_OPERATIONAL",
  "order-processing": "DESKTOP_ONLY",
  "unicommerce": "DESKTOP_ONLY",
  "marketplace-operations": "DESKTOP_ONLY",
  "marketplace-label-jobs": "DESKTOP_ONLY",
  "desktop-stations": "DESKTOP_ONLY",
  "ads-command-center": "DESKTOP_ONLY",
  "system-health": "DESKTOP_ONLY",
  "operations-settings": "DESKTOP_ONLY",
  "master-data": "DESKTOP_ONLY",
  "product-control": "DESKTOP_ONLY",
};

const enrichPage = (page) => {
  const domain = PAGE_DOMAIN_OVERRIDES[page.key] || GROUP_DOMAIN_DEFAULTS[page.group] || "settings";
  return {
    ...page,
    domain,
    subgroup: PAGE_SUBGROUP_OVERRIDES[page.key] || GROUP_SUBGROUP_DEFAULTS[page.group] || "Other",
    mobileMode: PAGE_MOBILE_OVERRIDES[page.key] || DOMAIN_MOBILE_DEFAULTS[domain] || "DESKTOP_ONLY",
  };
};

export const PAGE_REGISTRY = BASE_PAGE_REGISTRY.map(enrichPage);

/**
 * OMS menu sections (see omsSections.js). Each item carries the permission key and roles of the
 * existing page it opens, so the menu shows it to exactly the users who can already open that page.
 * A page's own entry keeps its registry key; shortcuts get their own key plus `permissionKey`.
 */
export function buildOmsSections() {
  const byKey = new Map(PAGE_REGISTRY.map((page) => [page.key, page]));
  const listed = new Set();
  const sections = OMS_SECTIONS.map((section) => ({
    id: `oms-${section.id}`,
    sectionId: section.id,
    label: section.label,
    path: section.path,
    icon: section.icon,
    items: section.items.flatMap((item) => {
      const page = byKey.get(item.pageKey);
      if (!page || page.hidden) return [];
      const own = item.id === page.key;
      if (own) listed.add(page.key);
      return [{
        ...(own ? page : {}),
        key: item.id,
        permissionKey: page.key,
        path: item.path,
        label: item.label,
        icon: item.icon || page.icon,
        roles: page.roles,
        domain: "oms",
        subgroup: section.label,
        omsSection: section.id,
        hint: item.hint,
        mobileMode: own ? page.mobileMode : "DESKTOP_ONLY",
        shortcut: !own,
      }];
    }),
  }));
  // An OMS page not yet placed in a section still shows, under OMS Settings.
  const settings = sections.find((section) => section.sectionId === "settings");
  for (const page of PAGE_REGISTRY) {
    if (page.hidden || page.domain !== "oms" || listed.has(page.key)) continue;
    settings.items.push({ ...page, permissionKey: page.key, subgroup: settings.label, omsSection: "settings", shortcut: false });
  }
  return sections.filter((section) => section.items.length > 0);
}

/** Landing pages of the OMS sections the user can see (mobile "More" and the command launcher). */
export function buildOmsLandingPages(domains) {
  const oms = (domains || []).find((domain) => domain.id === "oms");
  return (oms?.sections || []).filter((section) => section.path && section.items.length > 0).map((section) => ({
    key: `oms-section-${section.sectionId}`,
    path: section.path,
    label: section.label,
    icon: section.icon,
    domain: "oms",
    subgroup: "OMS",
    mobileMode: "MOBILE_VIEW_ONLY",
  }));
}

export function buildDomainNavigation() {
  return DOMAIN_DEFINITIONS.map((domain) => {
    if (domain.id === "oms") return { ...domain, sections: buildOmsSections() };
    const pages = PAGE_REGISTRY.filter((page) => !page.hidden && page.domain === domain.id);
    const bySection = new Map();
    for (const page of pages) {
      if (!bySection.has(page.subgroup)) bySection.set(page.subgroup, []);
      bySection.get(page.subgroup).push(page);
    }
    return {
      ...domain,
      sections: Array.from(bySection, ([label, items]) => ({
        id: `${domain.id}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`,
        label,
        items,
      })),
    };
  }).filter((domain) => domain.sections.length > 0);
}

export function getPageByPath(path) {
  if (!path) return undefined;
  const value = String(path);
  const exact = PAGE_REGISTRY.find((page) => page.path === value);
  if (exact) return exact;
  const pathname = value.split("?")[0].split("#")[0];
  return PAGE_REGISTRY.find((page) => page.path === pathname);
}

// All unique permission keys (for Select All / count)
export const ALL_PERMISSION_KEYS = PAGE_REGISTRY.map(p => p.key);

// Logical permissions — NOT in sidebar (no path), but exposed in User Permissions dialog
// so admin can mark a user as Picklist Operator 1 or Operator 2.
export const LOGICAL_PERMISSIONS = [
  { key: "picklist-operator-1", label: "Picklist — Operator 1", group: "Picklist Roles" },
  { key: "picklist-operator-2", label: "Picklist — Operator 2", group: "Picklist Roles" },
  { key: "picklist-final-view", label: "Picklist — Final View (PDF / WhatsApp share)", group: "Picklist Roles" },
  { key: "return-scan-edit",    label: "Return Scan — Edit / Delete", group: "Return Scanning" },
  { key: "inventory_valuation_export", label: "Inventory Valuation — Export Summary Excel", group: "Business Intelligence" },
  { key: "karigar_payment_leave_waive", label: "Karigar Payments — Waive Leave Deduction", group: "Finance" },

  // Marketplace / OMS / Automation granular permissions (Sep 2026)
  { key: "marketplace_view", label: "Marketplace — View connections/status", group: "Marketplace" },
  { key: "marketplace_configure", label: "Marketplace — Configure connections", group: "Marketplace" },
  { key: "marketplace_authorize", label: "Marketplace — Authorize/test connection", group: "Marketplace" },
  { key: "marketplace_sync", label: "Marketplace — Run read sync", group: "Marketplace" },
  { key: "orders_view", label: "OMS — View orders", group: "OMS" },
  { key: "orders_process", label: "OMS — Process orders", group: "OMS" },
  { key: "orders_ship", label: "OMS — Labels / shipping actions", group: "OMS" },
  { key: "orders_cancel", label: "OMS — Cancel orders", group: "OMS" },
  { key: "orders_retry", label: "OMS — Retry failed order jobs", group: "OMS" },
  { key: "automation_view", label: "Automation — View", group: "Automation" },
  { key: "automation_configure", label: "Automation — Configure schedules", group: "Automation" },
  { key: "automation_run", label: "Automation — Run now", group: "Automation" },
  { key: "automation_pause", label: "Automation — Pause / resume", group: "Automation" },
  { key: "automation_otp", label: "Automation — Submit OTP", group: "Automation" },

  // Size Chart granular permissions (Feb 2026)
  { key: "product_control_access",       label: "Product Control — Access module",            group: "Size Chart Permissions" },
  { key: "size_chart_view",              label: "Size Chart — View charts",                   group: "Size Chart Permissions" },
  { key: "size_chart_create",            label: "Size Chart — Create draft",                  group: "Size Chart Permissions" },
  { key: "size_chart_edit_draft",        label: "Size Chart — Edit draft",                    group: "Size Chart Permissions" },
  { key: "size_chart_edit_published",    label: "Size Chart — Edit published (with audit)",   group: "Size Chart Permissions" },
  { key: "size_chart_publish",           label: "Size Chart — Publish / Approve",             group: "Size Chart Permissions" },
  { key: "size_chart_delete",            label: "Size Chart — Delete",                        group: "Size Chart Permissions" },
  { key: "size_chart_export",            label: "Size Chart — Export CSV/Excel",              group: "Size Chart Permissions" },
  { key: "size_chart_copy",              label: "Size Chart — Copy from another style",       group: "Size Chart Permissions" },
  { key: "size_chart_link_shared",       label: "Size Chart — Link style to shared chart",    group: "Size Chart Permissions" },
  { key: "size_chart_unlink_shared",     label: "Size Chart — Unlink / Detach from shared",   group: "Size Chart Permissions" },
  { key: "size_chart_shared_manage",     label: "Size Chart — Manage shared charts (edit/delete)", group: "Size Chart Permissions" },
  { key: "size_chart_template_view",     label: "Size Chart — View templates",                group: "Size Chart Permissions" },
  { key: "size_chart_template_manage",   label: "Size Chart — Manage templates (create/edit/delete)", group: "Size Chart Permissions" },
  { key: "size_chart_audit_view",        label: "Size Chart — View audit / history",          group: "Size Chart Permissions" },
  { key: "size_chart_measurement_master_manage", label: "Size Chart — Manage Measurement Master (add/edit/delete)", group: "Size Chart Permissions" },
  { key: "size_chart_add_extra_measurement",  label: "Size Chart — Add extra measurement column (per chart)",  group: "Size Chart Permissions" },

  // Ads Command Center granular permissions (Phase 1, Feb 2026)
  { key: "ads_command_center_access",     label: "Ads — Access Command Center module",       group: "Ads Command Center" },
  { key: "ads_dashboard_view",            label: "Ads — View dashboard",                     group: "Ads Command Center" },
  { key: "ads_import",                    label: "Ads — Import CSV / manual entry",          group: "Ads Command Center" },
  { key: "ads_platform_view",             label: "Ads — View platform accounts",             group: "Ads Command Center" },
  { key: "ads_campaign_view",             label: "Ads — View campaigns",                     group: "Ads Command Center" },
  { key: "ads_sku_profit_view",           label: "Ads — View SKU / style profitability",     group: "Ads Command Center" },
  { key: "ads_keyword_view",              label: "Ads — View keyword intelligence",          group: "Ads Command Center" },
  { key: "ads_creative_view",             label: "Ads — View creative performance",          group: "Ads Command Center" },
  { key: "ads_market_trend_view",         label: "Ads — View market trend intelligence",     group: "Ads Command Center" },
  { key: "ads_ai_recommendation_view",    label: "Ads — View AI / rule recommendations",     group: "Ads Command Center" },
  { key: "ads_ai_recommendation_generate",label: "Ads — Generate AI recommendations (Phase 2+)", group: "Ads Command Center" },
  { key: "ads_campaign_draft_create",     label: "Ads — Create campaign draft (Phase 4+)",   group: "Ads Command Center" },
  { key: "ads_campaign_approve",          label: "Ads — Approve campaign / confirmation phrase",  group: "Ads Command Center" },
  { key: "ads_campaign_publish",          label: "Ads — Publish live campaign (Phase 5+)",   group: "Ads Command Center" },
  { key: "ads_budget_change_approve",     label: "Ads — Approve budget change",              group: "Ads Command Center" },
  { key: "ads_agency_view",               label: "Ads — View agency scorecard",              group: "Ads Command Center" },
  { key: "ads_agency_manage",             label: "Ads — Manage agencies (add/edit)",         group: "Ads Command Center" },
  { key: "ads_tasks_manage",              label: "Ads — Manage ads tasks",                   group: "Ads Command Center" },
  { key: "ads_reports_export",            label: "Ads — Export reports (CSV/Excel)",         group: "Ads Command Center" },
  { key: "ads_api_connection_manage",     label: "Ads — Manage API connections (Phase 3+)",  group: "Ads Command Center" },
  { key: "ads_ai_provider_manage",        label: "Ads — Manage AI provider settings (Phase 2+)", group: "Ads Command Center" },
  { key: "ads_settings_manage",           label: "Ads — Manage profit config & settings",    group: "Ads Command Center" },
];

export const ALL_LOGICAL_KEYS = LOGICAL_PERMISSIONS.map(p => p.key);

// Used by UsersPage to render full permission set
export const ALL_GRANTABLE_KEYS = [...ALL_PERMISSION_KEYS, ...ALL_LOGICAL_KEYS];

// Group order for sidebar navigation
const SIDEBAR_GROUP_ORDER = [
  "Core", "Cutting", "Production", "Fabric",
  "Special Entries", "People & HR", "Finance", "Dispatch", "Insights & Reports",
  "Picklist",
  "OTP Center",
  "Product Control",
  "Analytics",
  "Task Management",
  "Ads Command Center",
  "Settings",
];

// Groups that default open in sidebar
const DEFAULT_OPEN_GROUPS = ["Cutting", "Production"];

// Group icons for sidebar section headers
const GROUP_ICONS = {
  "Core":               LayoutDashboard,
  "Cutting":            Scissors,
  "Production":         Factory,
  "Fabric":             PackagePlus,
  "Special Entries":    Zap,
  "Finance":            IndianRupee,
  "People & HR":        Users,
  "Dispatch":           Truck,
  "Insights & Reports": Brain,
  "OTP Center":         ShieldCheck,
  "Product Control":    Ruler,
  "Analytics":          TrendingUp,
  "Task Management":    ClipboardCheck,
  "Settings":           Settings,
};

/**
 * Build sidebar sections from PAGE_REGISTRY
 * Returns array of { id, type, label, icon, defaultOpen, items[] }
 */
export function buildSidebarSections() {
  const grouped = {};
  for (const page of PAGE_REGISTRY) {
    if (page.hidden) continue;   // Hidden pages: valid route + permission, no sidebar
    if (!grouped[page.group]) grouped[page.group] = [];
    grouped[page.group].push(page);
  }

  return SIDEBAR_GROUP_ORDER
    .filter(g => grouped[g]?.length > 0)
    .map(groupName => {
      const items = grouped[groupName];
      // "Core" items rendered as direct links
      if (groupName === "Core") {
        return items.map(p => ({
          id: p.key,
          type: "link",
          path: p.path,
          key: p.key,
          label: p.label,
          icon: p.icon,
          roles: p.roles,
        }));
      }
      return {
        id: groupName.toLowerCase().replace(/\s+/g, "-"),
        type: "section",
        label: groupName,
        icon: GROUP_ICONS[groupName] || Settings,
        defaultOpen: DEFAULT_OPEN_GROUPS.includes(groupName),
        items: items.map(p => ({
          path: p.path,
          key: p.key,
          label: p.label,
          icon: p.icon,
          roles: p.roles,
        })),
      };
    })
    .flat();
}

/**
 * Build permission groups for UsersPage
 * Returns array of { group, items: [{key, label}] }
 */
export function buildPermissionGroups() {
  const grouped = {};
  const groupOrder = [];
  for (const page of PAGE_REGISTRY) {
    if (page.hidden) continue;   // Skip hidden pages in Admin → Users → Permissions UI
    if (!grouped[page.group]) {
      grouped[page.group] = [];
      groupOrder.push(page.group);
    }
    grouped[page.group].push({ key: page.key, label: page.label });
  }
  // Append logical permission groups (Picklist Roles etc.)
  for (const p of LOGICAL_PERMISSIONS) {
    if (!grouped[p.group]) {
      grouped[p.group] = [];
      groupOrder.push(p.group);
    }
    grouped[p.group].push({ key: p.key, label: p.label });
  }
  return groupOrder.map(g => ({ group: g, items: grouped[g] }));
}
